import type { FrameState } from 'ol/Map';
import VectorImageLayer from 'ol/layer/VectorImage';
import type VectorSource from 'ol/source/Vector';
import type { FeatureLike } from 'ol/Feature';

/**
 * V1.9.0 E4 · a VectorImage layer that can render its image denser than the screen (supersampling) and let the
 * browser scale it down. Used for the Maestro presentation while thin secondary outlines (below 2.5 px) are drawn on a
 * screen of device pixel ratio 1: a 1.5 px line otherwise falls on two half-covered pixels and loses its colour and its
 * light casing. Widths, dashes and geometries are unchanged; only the raster density of this layer's image (and its
 * cost) changes, and only while the factor is above 1. The visible frame and hit detection keep the real pixel ratio.
 */
export class SupersampledVectorImageLayer<F extends FeatureLike = FeatureLike> extends VectorImageLayer<VectorSource<F & import('ol/Feature').default>> {
  private supersample = 1;

  get supersampleFactor(): number { return this.supersample; }

  /** 1 = as the screen; 2 = twice the pixel density. A change re-renders the image once. */
  setSupersample(factor: number): void {
    const next = Math.max(1, Math.min(3, factor));
    if (next === this.supersample) return;
    this.supersample = next; this.changed();
  }

  override createRenderer() {
    const renderer = super.createRenderer();
    const prepare = renderer.prepareFrame.bind(renderer);
    // Only the image is prepared at the denser ratio; renderFrame keeps the real one and scales the image down.
    renderer.prepareFrame = (frameState: FrameState) => prepare(this.supersample === 1 ? frameState : { ...frameState, pixelRatio: frameState.pixelRatio * this.supersample });
    return renderer;
  }
}

/** Factor for the presentation: only thin outlines on a low-density screen pay for the extra pixels. */
export const outlineSupersample = (outlineWidth: number | null, devicePixelRatio: number): number =>
  outlineWidth !== null && outlineWidth > 0 && outlineWidth < 2.5 && devicePixelRatio < 2 ? 2 : 1;
